import React, { useEffect, useState } from 'react';
import ApiClient from '../../Services/ApiClient';
import { useAlert } from '../ui/alertProvider';

const SitesAdmin: React.FC = () => {
  const [sites, setSites] = useState<any[]>([]);
  const [settings, setSettings] = useState<any[]>([]);
  const [domain, setDomain] = useState('');
  const [siteName, setSiteName] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [settingKey, setSettingKey] = useState('');
  const [settingValue, setSettingValue] = useState('');
  const [dataType, setDataType] = useState('String');
  const { showAlert } = useAlert();

  const load = async () => {
    try {
      const s: any = await ApiClient.get('/api/sites');
      setSites(s || []);
    } catch (e) { console.error(e); }
    try {
      const st: any = await ApiClient.get('/api/settings');
      setSettings(st || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh after certain actions by polling every 30s while admin is on page
  useEffect(() => {
    const id = setInterval(() => { load(); }, 30000);
    return () => clearInterval(id);
  }, []);

  const createSite = async () => {
    if (!domain) { showAlert('Domain required', 'error'); return; }
    try {
      const body = { Domain: domain, SiteName: siteName || domain };
      const res = await ApiClient.post('/api/sites', body);
      showAlert('Site created', 'success');
      setDomain(''); setSiteName('');
      await load();
      // send audit log
      try { await ApiClient.post('/api/audit/logs', { Action: 'SiteCreated', Target: domain, Details: `Site created ${domain}` }); } catch {}
    } catch (e: any) { showAlert(e?.data || 'Failed to create site', 'error'); }
  };

  const createAndLinkSetting = async () => {
    if (!selectedSiteId) { showAlert('Select site first', 'error'); return; }
    if (!settingKey) { showAlert('Setting key required', 'error'); return; }
    try {
      const body = { SettingKey: settingKey, SettingValue: settingValue, DataType: dataType, Description: '' };
      // POST /api/sites/{siteId}/settings
      const res = await ApiClient.post(`/api/sites/${selectedSiteId}/settings`, body);
      showAlert('Setting created and linked to site', 'success');
      setSettingKey(''); setSettingValue('');
      await load();
    } catch (e: any) { showAlert(e?.data || 'Failed to create/link setting', 'error'); }
  };

  const linkExisting = async (settingId?: number) => {
    if (!selectedSiteId) { showAlert('Select site first', 'error'); return; }
    if (!settingId) { showAlert('Select a setting to link', 'error'); return; }
    try {
      await ApiClient.post(`/api/sites/${selectedSiteId}/link/${settingId}`);
      showAlert('Setting linked to site', 'success');
      await load();
    } catch (e: any) { showAlert(e?.data || 'Failed to link setting', 'error'); }
  };

  return (
    <div className="page-padding">
      <h2>Sites Administration</h2>

      <section className="mb-24">
        <h3>Create site (IP or domain)</h3>
        <div>
          <label>Domain / Hostname</label>
          <input value={domain} onChange={e => setDomain(e.target.value)} placeholder='e.g. 187.77.87.127' />
        </div>
        <div>
          <label>Site name (optional)</label>
          <input value={siteName} onChange={e => setSiteName(e.target.value)} placeholder='Site friendly name' />
        </div>
        <div className="mt-8">
          <button className="btn btn-primary" onClick={createSite}>Create site</button>
        </div>
      </section>

      <section className="mb-24">
        <h3>Existing sites</h3>
        <select value={selectedSiteId ?? ''} onChange={e => setSelectedSiteId(e.target.value ? Number(e.target.value) : null)}>
          <option value=''>-- select site --</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.siteName ?? s.domain} ({s.domain})</option>)}
        </select>
        <div className="mt-12">
          <h4>Create & link a new setting for selected site</h4>
          <div>
            <label>Key</label>
            <input value={settingKey} onChange={e => setSettingKey(e.target.value)} placeholder='MaxRegisteredSiteUsers' />
          </div>
          <div>
            <label>Value</label>
            <input value={settingValue} onChange={e => setSettingValue(e.target.value)} />
          </div>
          <div>
            <label>Data type</label>
            <select value={dataType} onChange={e => setDataType(e.target.value)}>
              <option>String</option>
              <option>Int</option>
              <option>Bool</option>
            </select>
          </div>
          <div className="mt-8">
            <button className="btn btn-primary" onClick={createAndLinkSetting}>Create & Link</button>
          </div>
        </div>

        <div className="mt-16">
          <h4>Or link an existing global setting</h4>
          <div>
            <select onChange={e => linkExisting(e.target.value ? Number(e.target.value) : undefined)}>
              <option value=''>-- select global setting to link --</option>
              {settings.map((st:any) => <option key={st.id} value={st.id}>{st.settingKey} = {st.settingValue}</option>)}
            </select>
          </div>
        </div>
      </section>

      <section>
        <h3>All sites</h3>
        <div className="table-container">
          <table className="items-table">
            <thead><tr><th>Id</th><th>Domain</th><th>Name</th></tr></thead>
            <tbody>
              {sites.map(s => (
                <tr key={s.id}><td>{s.id}</td><td>{s.domain}</td><td>{s.siteName}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default SitesAdmin;
